import {action, computed, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import type {IAuthApiProvider, ILoginPayload, IProfile} from "data/providers/api/auth.api.provider";
import {Bindings} from "data/constants/bindings";

export interface IUser {
	id: number;
	email: string;
	username: string;
	firstname: string;
	lastname: string;
	country: string;
	isNotificationsEnabled: boolean;
	terms: null | boolean;
	optInGZ: boolean;
}

export interface IUpdateUserData {
	email?: string | null;
	password?: string | null;
	username?: string | null;
	isNotificationsEnabled?: boolean | null;
	terms?: null | boolean;
	optIn?: null | boolean;
}

export interface IUserStore {
	get user(): IUser | undefined;

	get profile(): IProfile | undefined;

	get fullName(): string;

	get isAuthorized(): boolean;

	get wasLoggedOut(): boolean;

	get isRequestingUser(): boolean;

	get didRequestRorUser(): boolean;

	login(payload: ILoginPayload): Promise<void>;

	logout(): Promise<void>;

	fetchUser(): Promise<void>;

	fetchUserProfile(): Promise<void>;

	update(payload: IUpdateUserData): Promise<void>;
}

@injectable()
export class UserStore implements IUserStore {
	constructor(@inject(Bindings.AuthApiProvider) private _authApi: IAuthApiProvider) {
		makeAutoObservable(this);
	}

	@observable private _isRequestingUser: boolean = false;
	@observable private _didRequestRorUser: boolean = false;

	get isRequestingUser(): boolean {
		return this._isRequestingUser;
	}

	get didRequestRorUser(): boolean {
		return this._didRequestRorUser;
	}

	@observable private _user?: IUser = undefined;
	@observable private _profile?: IProfile = undefined;

	get user() {
		return this._user;
	}

	get profile() {
		return this._profile;
	}

	@observable private _isAuthorized = false;

	get isAuthorized(): boolean {
		return this._isAuthorized;
	}

	@observable private _wasLoggedOut = false;

	get wasLoggedOut(): boolean {
		return this._wasLoggedOut;
	}

	@computed get fullName() {
		if (!this._user) {
			return "";
		}

		const {firstname, lastname} = this._user;
		return `${firstname} ${lastname}`;
	}

	@action
	async login(payload: ILoginPayload): Promise<void> {
		const response = await this._authApi.login(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
			this._isAuthorized = true;
			this._wasLoggedOut = false;
		});
	}

	@action
	public async fetchUser(): Promise<void> {
		runInAction(() => {
			this._didRequestRorUser = true;
		});

		try {
			this.changeLoadingState(true);
			const response = await this._authApi.fetchUser();
			const {user} = response.data.success;

			runInAction(() => {
				this._user = user;
				this._isAuthorized = true;
				this._wasLoggedOut = false;
			});
		} catch (e) {
			// Do nothing. This is expected error
		} finally {
			this._didRequestRorUser = false;
			this.changeLoadingState(false);
		}
	}

	@action
	public async fetchUserProfile(): Promise<void> {
		runInAction(() => {
			this._didRequestRorUser = true;
		});

		try {
			this.changeLoadingState(true);
			const response = await this._authApi.fetchUserProfile();
			const {profile} = response.data.success;

			runInAction(() => {
				this._profile = profile;
				this._isAuthorized = true;
				this._wasLoggedOut = false;
			});
		} catch (e) {
			return Promise.reject(e);
		} finally {
			this.changeLoadingState(false);
		}
	}

	@action
	async logout() {
		await this._authApi.logout();

		runInAction(() => {
			this._user = undefined;
			this._isAuthorized = false;
			this._wasLoggedOut = true;
		});

		window.location.href = process.env.REACT_APP_GZ_LINK || "";
	}

	@action
	private changeLoadingState(value: boolean): void {
		runInAction(() => {
			this._isRequestingUser = value;
		});
	}

	@action
	async update(payload: IUpdateUserData): Promise<void> {
		const response = await this._authApi.update(payload);
		const {profile} = response.data.success;

		runInAction(() => {
			this._user = {
				...this._user,
				...profile,
			} as IUser;
		});
	}
}
